import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup'
import { Autocomplete, Box, Button, FormHelperText, Grid, TextField } from "@mui/material";
import Header from "components/Header";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BasePathProvider, useBasePath } from "components/Providers";
import { save } from "store/asset/asset-assign";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import StudentSearchBar from "components/StudentSearch";
import StaffSearch from "components/StaffSearch";
import { getRoom } from "store/lookup";
import { capitalize } from 'lodash'

export default function AssignAssetForm({ asset, assetCondition, action }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const basePath = useBasePath()
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const rooms = useSelector(state => state.lookup.room)
    const [selectedPerson, setSelectedPerson] = useState(null)
    const { id } = useParams()

    const statusMap = {
        assign: 'assigned',
        return: 'unassigned',
        loan: 'loaned',
        lost: 'lost',
        restock: 'unassigned',
        deprovisioned: 'deprovisioned',
      };

    const statusId = statusMap[action] || '';

    useEffect(() => {
        if (!rooms) {
            dispatch(getRoom())
        }
    }, [rooms, dispatch])

    const needsAssignee = action === 'assign' || action === 'loan'

    const schema = yup.object().shape({
        assigneeType: needsAssignee
            ? yup.string().required('Assignee Type is required')
            : yup.string().nullable(),
        assigneeId: yup.string().nullable().when('assigneeType', {
            is: (val) => val === 'student' || val === 'staff',
            then: yup.string().nullable().required('Assignee is required'),
        }),
        roomId: yup.string().nullable().when('assigneeType', {
            is: 'room',
            then: yup.string().nullable().required('Room is required'),
        }),
    });

    const { control, handleSubmit, formState, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...asset,
            assigneeId: null,
            roomId: null,
            assignedDate: dayjs(),
        }
    })

    const onSubmit = async data => {
        const formattedDate = dayjs(data.assignedDate).format('YYYY-MM-DD')
        const payload = {
            id,
            assigneeId: selectedPerson ? selectedPerson.personId : null,
            roomId: selectedAssignee?.value === 'room' ? Number(data.roomId) : null,
            conditionId: data.conditionId,
            assignedDate: (action === 'assign' || action === 'loan') ? formattedDate : null,
            statusId,
            note: data.note,
            action,
            ...(action === 'repair' && { inRepair: true, assigneeId: asset.assigneeId, roomId: asset.roomId, assignedDate: asset.assignedDate, statusId: asset.statusId }),
            ...(action === 'repaired' && { inRepair: false, assigneeId: asset.assigneeId, roomId: asset.roomId, assignedDate: asset.assignedDate, statusId: asset.statusId }),
        }

        const res = await dispatch(save(payload))
        if (res.payload) {
            navigate(basePath)
        }
    }

    const handleAssigneeChange = (event, value) => {
        setSelectedAssignee(value);
        if (value?.value === 'student' || value?.value === 'staff') {
            setSelectedPerson(null);
            setValue("assigneeId", null);
        } else {
            setValue("roomId", null);
        }
    };

    const assigneeTypes = [
        { value: 'staff', label: 'Staff' },
        { value: 'student', label: 'Student' },
        { value: 'room', label: 'Room' }
      ];


    return (
        <BasePathProvider value={`${basePath}`}>
            <Box>
                <Header title={`${capitalize(action)} ${asset.brand} - SN: ${asset.serialNumber}`} small close="." borderBottom />
            </Box>
            <Box data-component="AssgnAssetForm" component="form" onSubmit={handleSubmit(onSubmit)} py={2} px={1} >
                <Grid container spacing={1.5}>
                    {(action === 'assign' || action === 'loan') && (
                        <Grid item xs={12} >
                            <Controller
                                name="assigneeType"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                    options={assigneeTypes}
                                    getOptionLabel={(option) => option.label}
                                    value={assigneeTypes.find(option => option.value === field.value) ?? null}
                                    onChange={(event, value) => {
                                        field.onChange(value ? value.value : '')
                                        handleAssigneeChange(event, value)
                                    }}
                                    onBlur={field.onBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Assignee Type"
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                                    fullWidth
                                />
                                )}
                            />
                        </Grid>
                    )}
                    {selectedAssignee && selectedAssignee.value === 'student' && (action === 'assign' || action === 'loan') && (
                        <Grid item xs={12} >
                            <Controller
                                name="assigneeId"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <>
                                        <StudentSearchBar
                                            label="Select Student"
                                            size="small"
                                            onSelect={person => {
                                                setSelectedPerson(person)
                                                field.onChange(person ? person.personId : null)
                                            }}
                                            value={selectedPerson}
                                        />
                                        {fieldState.error && (
                                            <FormHelperText error sx={{ mx: 1.75 }}>
                                                {fieldState.error.message}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </Grid>
                    )}    
                    {selectedAssignee && selectedAssignee.value === 'staff' && (action === 'assign' || action === 'loan') && (
                        <Grid item xs={12} >
                            <Controller
                                name="assigneeId"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <>
                                        <StaffSearch
                                            label="Select Staff"
                                            size="small"
                                            onSelect={person => {
                                                setSelectedPerson(person)
                                                field.onChange(person ? person.personId : null)
                                            }}
                                            value={selectedPerson}
                                        />
                                        {fieldState.error && (
                                            <FormHelperText error sx={{ mx: 1.75 }}>
                                                {fieldState.error.message}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </Grid>
                    )}    
                    {selectedAssignee && selectedAssignee.value === 'room' && (action === 'assign' || action === 'loan') && (
                        <Grid item xs={12} >
                        <Controller
                            name="roomId"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                options={rooms}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                value={rooms.find((option) => option.id === field.value) ?? null}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue ? newValue.id : null)
                                }}
                                onBlur={field.onBlur}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Room"
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                                fullWidth
                              />
                            )}
                        />
                        </Grid>
                    )}    
                    <Grid item xs={12} >
                        <Controller
                            name="conditionId"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    value={assetCondition.find(option => option.code === field.value)}
                                    options={assetCondition}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('Condition')}
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!formState.errors.typeId}
                                            helperText={formState.errors.typeId ? formState.errors.typeId.message : ''}
                                        />
                                    )}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue ? newValue.code : null)
                                    }}
                                    onBlur={() => {
                                        field.onBlur()
                                    }}
                                    noOptionsText={t('No options')}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="assignedDate"
                            control={control}
                            render={({ field: { value, ...field }, fieldState }) => {
                                return (
                                    <DatePicker
                                        sx={{ width: '100%' }}
                                        slotProps={{ textField: { size: 'small' } }}
                                        label={t('Assign Date')}
                                        error={!!fieldState.error}
                                        size="small"
                                        value={dayjs(value) || null}
                                        {...field}
                                        disabled={action === 'return'}
                                    />
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Notes')}
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                    <Button
                        size="small"
                        variant="contained"
                        color="inherit"
                        onClick={() => {
                            navigate(basePath)
                        }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.isDirty}
                    >
                        {t('Save')}
                    </Button>
                </Box>
            </Box>
        </BasePathProvider>
    );
}